import { Directive, OnInit, ViewChild, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import {
  StripeSubscriptionStatus,
  SubscriptionAdminFilterKey,
  SubscriptionSummaryDto,
} from '@workspace/data-types';
import {
  ActionButton,
  ActionMenu,
  BaseDataTableComponent,
  DataHeaderFilter,
  DataListColumn,
} from '@workspace/ng-aura';
import { subscriptionStatusDisplayTransform } from '@workspace/ng-data-access';
import { Observable } from 'rxjs';

export type SubscriptionColumnKey =
  'user' | 'plan' | 'status' | 'currentPeriodStart' | 'currentPeriodEnd' | 'cancelAtPeriodEnd' | 'createdAt';

@Directive()
export abstract class AbstractSubscriptionsDataTableCoreComponent<T extends SubscriptionSummaryDto>
  extends BaseDataTableComponent<T, SubscriptionColumnKey>
  implements OnInit
{
  override topMenu = signal<ActionMenu | null>(null);
  override columns = signal<DataListColumn<SubscriptionColumnKey>[]>([
    { key: 'user', label: 'Utilisateur', display: true },
    { key: 'plan', label: 'Offre', display: true },
    { key: 'status', label: 'Status', display: true },
    { key: 'currentPeriodStart', label: 'Début de période', display: false },
    { key: 'currentPeriodEnd', label: 'Fin de période', display: true },
    { key: 'cancelAtPeriodEnd', label: 'Annulation en fin de période', display: true },
    { key: 'createdAt', label: 'Date de création', display: false },
  ]);
  override topButtons = signal<ActionButton[]>([]);
  override itemActions = signal<ActionButton[]>([
    {
      label: 'Ouvrir',
      icon: 'open_in_new',
      onClick: (sub: SubscriptionSummaryDto) => this.navigateToSubscription(sub),
      disabled: false,
      color: 'primary',
    },
  ]);

  override filters = signal<DataHeaderFilter<SubscriptionAdminFilterKey>[]>([
    {
      label: 'Status',
      column: SubscriptionAdminFilterKey.Status,
    },
  ]);

  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const status = params.getAll(SubscriptionAdminFilterKey.Status);
      this.filterChanged({
        changed: true,
        filter: {
          status: status.length ? status : [],
        },
      });
    });
  }

  navigateToSubscription(sub: SubscriptionSummaryDto): void {
    if (sub?._id) {
      void this.router.navigate(['/subscriptions', sub._id]);
    }
  }

  navigateToUser(sub: SubscriptionSummaryDto): void {
    if (sub?.user?._id) {
      void this.router.navigate(['/users', sub.user._id]);
    }
  }

  abstract override getAll(): Observable<T[]>;
  abstract override countAll(): Observable<number>;
  override getDisplayForEntry(key: SubscriptionAdminFilterKey, rawValue: string): string {
    switch (key) {
      case SubscriptionAdminFilterKey.Status:
        return subscriptionStatusDisplayTransform(rawValue as StripeSubscriptionStatus);
      default:
        return key;
    }
  }
}
